/**
 * Child's local progress — versioned-schema localStorage.
 * No accounts or sync: losing it when clearing data is acceptable (monetization
 * has its own restore via IAP in M4).
 */

const STORAGE_KEY = "melodimals.progress.v1";

export interface Progress {
  /** Highest level reached per game (module id → level). */
  bestLevel: Record<string, number>;
  /** Songs completed in "your turn" mode. */
  songsCompleted: string[];
  /**
   * Collection of earned stickers/medals (STICKERS ids), without duplicates.
   * It is the visible, persistent reward for 2-6-year-olds: every completed
   * activity awards its sticker and accumulates it in /collection.
   */
  stickers: string[];
  /**
   * Days (local key `YYYY-MM-DD`) on which the child completed an activity,
   * without duplicates. It feeds gamification-core's friendly "game streak"
   * (a pure projection over these days). Backward compatible: old progress
   * without this field is read as `[]`.
   */
  playDays: string[];
}

const EMPTY: Progress = { bestLevel: {}, songsCompleted: [], stickers: [], playDays: [] };

interface StorageLike {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
}

export interface ProgressStore {
  load: () => Progress;
  recordLevel: (gameId: string, level: number) => Progress;
  recordSongCompleted: (songId: string) => Progress;
  /**
   * Awards a sticker to the collection. Returns `{ progress, isNew }`:
   * `isNew` is true only the first time that sticker is earned, so the UI
   * can play the new-reward animation without repeating it on every review.
   */
  awardSticker: (stickerId: string) => { progress: Progress; isNew: boolean };
  /**
   * Marks a day (local key `YYYY-MM-DD`) as "played". Idempotent: repeating
   * the same day does not duplicate it. It feeds gamification-core's game streak.
   */
  recordPlayDay: (day: string) => Progress;
}

export function createProgressStore(storage?: StorageLike): ProgressStore {
  const backend: StorageLike | null =
    storage ?? (typeof localStorage === "undefined" ? null : localStorage);

  const read = (): Progress => {
    if (!backend) {
      return structuredClone(EMPTY);
    }
    try {
      const raw = backend.getItem(STORAGE_KEY);
      if (!raw) {
        return structuredClone(EMPTY);
      }
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== "object" || parsed === null) {
        return structuredClone(EMPTY);
      }
      const record = parsed as Partial<Progress>;
      return {
        bestLevel: isLevelMap(record.bestLevel) ? record.bestLevel : {},
        songsCompleted: isStringArray(record.songsCompleted) ? record.songsCompleted : [],
        stickers: isStringArray(record.stickers) ? record.stickers : [],
        playDays: isStringArray(record.playDays) ? record.playDays : [],
      };
    } catch {
      return structuredClone(EMPTY);
    }
  };

  const write = (progress: Progress) => {
    try {
      backend?.setItem(STORAGE_KEY, JSON.stringify(progress));
    } catch {
      // storage full/blocked: progress lives only in session memory
    }
  };

  return {
    load: read,
    recordLevel: (gameId, level) => {
      const progress = read();
      const best = progress.bestLevel[gameId] ?? 0;
      if (level > best) {
        progress.bestLevel[gameId] = level;
        write(progress);
      }
      return progress;
    },
    recordSongCompleted: (songId) => {
      const progress = read();
      if (!progress.songsCompleted.includes(songId)) {
        progress.songsCompleted.push(songId);
        write(progress);
      }
      return progress;
    },
    awardSticker: (stickerId) => {
      const progress = read();
      if (progress.stickers.includes(stickerId)) {
        return { progress, isNew: false };
      }
      progress.stickers.push(stickerId);
      write(progress);
      return { progress, isNew: true };
    },
    recordPlayDay: (day) => {
      const progress = read();
      if (!progress.playDays.includes(day)) {
        progress.playDays.push(day);
        write(progress);
      }
      return progress;
    },
  };
}

function isLevelMap(value: unknown): value is Record<string, number> {
  return (
    typeof value === "object" &&
    value !== null &&
    Object.values(value).every((v) => typeof v === "number")
  );
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === "string");
}
